import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { todayISO } from '@/lib/utils';

// ─── Types ─────────────────────────────────────────────────────────────────────

export type Preset = 'today' | 'week' | 'month' | 'all' | 'custom';

// ─── Date helpers ────────────────────────────────────────────────────────────────

/** Local date → YYYY-MM-DD (en-CA formats as ISO). */
export function isoOf(d: Date): string {
  return d.toLocaleDateString('en-CA');
}

/** First day of the current month. */
export function startOfMonth(): string {
  const now = new Date();
  return isoOf(new Date(now.getFullYear(), now.getMonth(), 1));
}

/** Monday of the current week. */
export function startOfWeek(): string {
  const now = new Date();
  const day = now.getDay(); // 0 = Sun … 6 = Sat
  const diff = day === 0 ? 6 : day - 1; // days since Monday
  return isoOf(new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff));
}

/**
 * Resolve a preset (+ custom inputs) to concrete `{from, to}` bounds.
 * "All" → empty bounds, which every report endpoint treats as no date filter
 * (all-time). Empty bounds are intentional, not a "not ready yet" signal — see
 * {@link rangeReady}.
 */
export function boundsFor(
  preset: Preset,
  customFrom: string,
  customTo: string,
): { from: string; to: string } {
  switch (preset) {
    case 'today':
      return { from: todayISO(), to: todayISO() };
    case 'week':
      return { from: startOfWeek(), to: todayISO() };
    case 'month':
      return { from: startOfMonth(), to: todayISO() };
    case 'all':
      return { from: '', to: '' };
    case 'custom':
      return { from: customFrom, to: customTo };
  }
}

/**
 * Whether the query should run. Every preset except "Custom" is always ready
 * (including "All", whose empty bounds are a valid all-time request). "Custom"
 * waits for both endpoints to be filled in.
 */
export function rangeReady(preset: Preset, customFrom: string, customTo: string): boolean {
  return preset === 'custom' ? Boolean(customFrom && customTo) : true;
}

export const PRESETS: { value: Preset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'all', label: 'All' },
  { value: 'custom', label: 'Custom' },
];

// ─── Component ───────────────────────────────────────────────────────────────────

interface DateRangePresetsProps {
  preset: Preset;
  onPresetChange: (preset: Preset) => void;
  customFrom: string;
  onCustomFromChange: (value: string) => void;
  customTo: string;
  onCustomToChange: (value: string) => void;
  /** Unique prefix for the custom From/To input ids (e.g. "is", "cs"). */
  idPrefix: string;
}

/**
 * Shared date-range preset bar: the Today / This week / This month / All / Custom
 * chip group, followed by the custom From/To date inputs (shown only for "Custom").
 *
 * Returns a fragment so the caller controls the surrounding flex row and can keep
 * its own per-report controls (e.g. a sort selector) alongside it.
 */
export default function DateRangePresets({
  preset,
  onPresetChange,
  customFrom,
  onCustomFromChange,
  customTo,
  onCustomToChange,
  idPrefix,
}: DateRangePresetsProps) {
  return (
    <>
      <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5">
        {PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => onPresetChange(p.value)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              preset === p.value
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {preset === 'custom' && (
        <>
          <div className="flex items-center gap-2">
            <Label htmlFor={`${idPrefix}-from`} className="text-sm text-muted-foreground">
              From
            </Label>
            <Input
              id={`${idPrefix}-from`}
              type="date"
              value={customFrom}
              onChange={(e) => onCustomFromChange(e.target.value)}
              className="w-36 h-8 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor={`${idPrefix}-to`} className="text-sm text-muted-foreground">
              To
            </Label>
            <Input
              id={`${idPrefix}-to`}
              type="date"
              value={customTo}
              onChange={(e) => onCustomToChange(e.target.value)}
              className="w-36 h-8 text-sm"
            />
          </div>
        </>
      )}
    </>
  );
}
